'use client'

import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { z } from 'zod'

/* 소설 모드 뷰어 설정 — 리디북스 뷰어 설정 구성 그대로(flow-renew ③).
   대화방 소설 모드 · 엔딩 소설(⑦) · 라운지 회차 보기(⑧)가 같은 값을 쓴다. 대화방마다 고른 보기(채팅/소설)도 여기 둔다.

   [임시 — 서버 연동 전] 설정은 계정에 저장해야 한다(기기가 바뀌어도 유지). 지금은 이 기기(localStorage)에 둔다.
   실제 계약: GET /user/settings/ → { reader: ReaderSettings } · PATCH /user/settings/ { reader: Partial<ReaderSettings> }
   연동할 때는 setSetting · resetSetting 뒤에 PATCH 를 보내고, 로그인 직후 서버 값으로 settings 를 덮는다(views 는 기기 값 그대로). */

/** 바탕 7색 — 리디 순서. 기본은 검정 */
export type ReaderPaper =
  'white' | 'cream' | 'lightgray' | 'gray' | 'darkgray' | 'green' | 'black'
export type ReaderFont = 'serif' | 'sans'
/** 원본 = 작품 그대로(지문 양쪽 맞춤 · 대사 왼쪽) · 양쪽 = 전부 양쪽 맞춤 · 왼쪽 = 전부 왼쪽 */
export type ReaderAlign = 'original' | 'justify' | 'left'
export type ReaderView = 'chat' | 'novel'

export type ReaderSettings = {
  paper: ReaderPaper
  /** 밝기 30~100 — 100 아래면 본문 위에 검은 막을 덮는다(화면 밝기 API 없이) */
  brightness: number
  font: ReaderFont
  /** 글자 크기 1~9 — 5 가 기본(모바일 17 · PC 19px) */
  size: number
  /** 줄 간격 1~5 — 3 이 기본(1.9) */
  lineGap: number
  /** 문단 간격 1~5 */
  paraGap: number
  /** 문단 너비 1~5 — 좁게 ~ 넓게, 3 이 기본(최대 680px) */
  width: number
  align: ReaderAlign
  /** 발췌(⑤)로 칠한 문장을 본문에 형광펜으로 */
  highlight: boolean
}

export const READER_DEFAULTS: ReaderSettings = {
  paper: 'black',
  brightness: 100,
  font: 'serif',
  size: 5,
  lineGap: 3,
  paraGap: 3,
  width: 3,
  align: 'original',
  highlight: true,
}

/** 바탕 · 글자색 — 글자색은 바탕에 맞춰 자동(밝은 바탕 = 진한 먹색, 어두운 바탕 = 따뜻한 흰색) */
export const PAPERS: Record<
  ReaderPaper,
  { label: string; bg: string; fg: string; dark: boolean }
> = {
  white: { label: '하양', bg: '#ffffff', fg: '#222222', dark: false },
  cream: { label: '미색', bg: '#f4ecd8', fg: '#3a3127', dark: false },
  lightgray: { label: '연회색', bg: '#e6e6e8', fg: '#232325', dark: false },
  gray: { label: '회색', bg: '#a9a9ad', fg: '#1d1d1f', dark: false },
  darkgray: { label: '짙은 회색', bg: '#3a3a3c', fg: '#ece8e1', dark: true },
  green: { label: '짙은 초록', bg: '#2d4a44', fg: '#ece8e1', dark: true },
  black: { label: '검정', bg: '#000000', fg: '#ece8e1', dark: true },
}
export const PAPER_ORDER: ReaderPaper[] = [
  'white',
  'cream',
  'lightgray',
  'gray',
  'darkgray',
  'green',
  'black',
]

/** 명조 — 웹폰트를 새로 받지 않고 기기에 있는 명조를 차례로 찾는다 */
export const SERIF_STACK =
  "'Noto Serif KR', 'Nanum Myeongjo', 'NanumMyeongjo', 'AppleMyungjo', 'Batang', 'Noto Serif CJK KR', serif"
/** 고딕 — 앱 글꼴(Pretendard) */
const SANS_STACK = 'var(--font-pretendard), -apple-system, sans-serif'

export const READER_FONTS: Record<
  ReaderFont,
  { label: string; family: string }
> = {
  serif: { label: '명조', family: SERIF_STACK },
  sans: { label: '고딕', family: SANS_STACK },
}

/** 단계 → 값. 글자 크기는 기본 글자(17/19px)에 곱한다 */
export const SIZE_SCALE = [0.76, 0.82, 0.88, 0.94, 1, 1.07, 1.15, 1.25, 1.37]
export const LINE_HEIGHTS = [1.6, 1.75, 1.9, 2.05, 2.2]
/** 문단 사이 — 글자 크기의 배수 */
export const PARA_GAPS = [0.25, 0.55, 0.9, 1.3, 1.8]
/** 문단 너비 — PC 최대 폭 · 모바일 좌우 여백 */
export const WIDTHS = [
  { max: 520, pad: 30 },
  { max: 600, pad: 24 },
  { max: 680, pad: 18 },
  { max: 760, pad: 12 },
  { max: 860, pad: 6 },
]

/** 기기 저장값은 믿을 수 없는 경계 — 검사해서 틀린 값이 섞여 있으면 그 묶음은 기본값으로 둔다 */
const step = z.number().int().min(1)
const savedSchema = z.object({
  settings: z
    .object({
      paper: z.enum([
        'white',
        'cream',
        'lightgray',
        'gray',
        'darkgray',
        'green',
        'black',
      ]),
      brightness: z.number().min(30).max(100),
      font: z.enum(['serif', 'sans']),
      size: step.max(9),
      lineGap: step.max(5),
      paraGap: step.max(5),
      width: step.max(5),
      align: z.enum(['original', 'justify', 'left']),
      highlight: z.boolean(),
    })
    .partial()
    .catch({}),
  views: z.record(z.string(), z.enum(['chat', 'novel'])).catch({}),
})

type ReaderState = {
  settings: ReaderSettings
  /** 대화방마다 고른 보기 — 키는 방 id */
  views: Record<string, ReaderView>
  setSetting: <K extends keyof ReaderSettings>(
    key: K,
    value: ReaderSettings[K]
  ) => void
  resetSetting: (key: keyof ReaderSettings) => void
  setView: (roomKey: string, view: ReaderView) => void
}

export const useReaderStore = create<ReaderState>()(
  persist(
    (set) => ({
      settings: READER_DEFAULTS,
      views: {},
      setSetting: (key, value) =>
        set((s) => ({ settings: { ...s.settings, [key]: value } })),
      resetSetting: (key) =>
        set((s) => ({
          settings: { ...s.settings, [key]: READER_DEFAULTS[key] },
        })),
      setView: (roomKey, view) =>
        set((s) => ({ views: { ...s.views, [roomKey]: view } })),
    }),
    {
      name: 'veluvit_reader',
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ settings: s.settings, views: s.views }),
      // 저장값에 새 항목이 없으면(옛 버전) 기본값으로 채운다
      merge: (persisted, current) => {
        const saved = savedSchema.safeParse(persisted)
        if (!saved.success) return current
        return {
          ...current,
          settings: { ...READER_DEFAULTS, ...saved.data.settings },
          views: saved.data.views,
        }
      },
    }
  )
)
